import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { MdCheckBox, MdCheckBoxOutlineBlank } from "react-icons/md";
import Button from "../../../../components/buttons/button";
import {
  useAddPlaylistMutation,
  useUpdatePlaylistMutation,
} from "../../../../store/Reducer/playlist";
import toast from "react-hot-toast";

export default function AddPlaylistModal({
  isOpen,
  closeModal,
  isEdit,
  rowData,
  deviceId,
}) {
  const { t } = useTranslation();
  const [selectedTheme, setSelectedTheme] = useState("#000000");
  const [name, setName] = useState("");
  const [isActive, setIsActive] = useState(true);

  const [addPlaylist, { isLoading }] = useAddPlaylistMutation();
  const [updatePlaylist, { isLoading: updatePlaylistLoading }] =
    useUpdatePlaylistMutation();

  const [displayItems, setDisplayItems] = useState({
    nextPrayer: false,
    clock: false,
    announcement: false,
    image: false,
    hadithOfTheDay: false,
    verseOfTheDay: false,
    events: false,
  });

  const handleThemeChange = (e) => {
    setSelectedTheme(e.target.value);
  };
  const handleNamechange = (e) => {
    setName(e.target.value);
  };

  const handleCheckboxChange = (item) => {
    setDisplayItems((prev) => ({
      ...prev,
      [item]: !prev[item],
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      const displayData = {
        name: name.trim(),
        themeColor: selectedTheme,
        itemsToDisplay: displayItems,
        isActive:isActive,
        id: isEdit ? rowData?._id : undefined, 
      };

      // Use updateDisplay if it's edit mode, otherwise use addDisplay for creating a new entry
      if (isEdit) {
        await updatePlaylist(displayData).unwrap();
        toast.success("Playlist updated successfully!");
      } else {
        await addPlaylist(displayData).unwrap();
        toast.success("Playlist added successfully!");
      }

      closeModal(); // Close the modal after successful submission
    } catch (error) {
      console.log("Error submitting form:", error);
      toast.error(error?.data?.message || "An error occurred!");
    }
  };
  // Update existing effect to only set form data when modal opens for editing
  useEffect(() => {
    if (isOpen && isEdit && rowData) {
      setSelectedTheme(rowData?.themeColor);
      setDisplayItems(rowData?.itemsToDisplay);
      setIsActive(rowData?.isActive);
      setName(rowData?.name)
    }
  }, [isOpen, isEdit, rowData]);

  useEffect(() => {
    if (!isOpen) {
      // Reset to the same defaults the form starts with. An empty theme colour
      // still shows as black in the colour picker, but the backend rejects it.
      setDisplayItems({});
      setSelectedTheme("#000000");
      setName("");
      setIsActive(true);
    }
  }, [isOpen]);

  const displayOptions = [
    { id: "nextPrayer", label: "Next prayer" },
    { id: "clock", label: "Clock" },
    { id: "announcement", label: "Announcement" },
    { id: "image", label: "Image" },
    { id: "hadithOfTheDay", label: "Hadith of the day" },
    { id: "verseOfTheDay", label: "Verse of the day" },
    { id: "events", label: "Events" },
  ];

  return (
    <>
      {isOpen && (
        <div
          className="fixed inset-0 overflow-y-auto bg-black bg-opacity-70 flex items-center justify-center z-50 mt-0 p-4 sm:p-0"
          onClick={closeModal}
        >
          <div
            className="rounded-lg shadow-xl w-full max-w-lg mx-auto h-full sm:h-auto sm:max-h-[90vh] flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="mx-auto w-full bg-white rounded-lg shadow-lg p-4 sm:px-6 sm:py-6">
              <div>
                <h1 className="text-2xl font-semibold text-gray-900 mb-6">
                  {isEdit ? "Update Playlist" : "Add Playlist"}
                </h1>

                {(isLoading || updatePlaylistLoading) && (
                  <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="loader border-t-4 border-blue-500 rounded-full w-16 h-16 animate-spin"></div>
                    <p className="text-white mt-4">Loading...</p>
                  </div>
                )}

                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="space-y-4">
                    <div>
                      <label htmlFor="playlist-name" className="block text-sm font-medium text-gray-700 mb-2">
                        Name
                      </label>
                      <input
                        id="playlist-name"
                        type="text"
                        value={name}
                        onChange={handleNamechange}
                        className="h-10 w-full rounded-md border border-gray-300 p-1 cursor-pointer"
                        required
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Select theme color
                      </label>
                      <input
                        type="color"
                        value={selectedTheme}
                        onChange={handleThemeChange}
                        className="h-10 w-full rounded-md border border-gray-300 p-1 cursor-pointer"
                        required
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Select items below to display
                      </label>
                      <div className="space-y-3">
                        {displayOptions.map((option) => (
                          <div
                            key={option.id}
                            className="flex items-center cursor-pointer"
                            onClick={() => handleCheckboxChange(option.id)}
                          >
                            {displayItems[option.id] ? (
                              <MdCheckBox className="w-5 h-5 text-blue-600" />
                            ) : (
                              <MdCheckBoxOutlineBlank className="w-5 h-5 text-gray-400" />
                            )}
                            <span className="ml-2 text-gray-700">
                              {option.label}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>

                  <div className="flex justify-center sm:justify-end items-center flex-wrap space-x-3 pt-4">
                    <button
                      type="button"
                      onClick={closeModal}
                      className="px-4 py-2 rounded-lg font-semibold text-sm transition-colors duration-200 ease-in-out focus:outline-none flex items-center text-gray-800 border border-gray-700 hover:bg-gray-100 justify-center"
                    >
                      {t("universal_cancel_text")}
                    </button>

                    <Button
                      text={t("universal_save_text")}
                      type="submit"
                      className="px-6 text-nowrap"
                    />

                    {isEdit && (
                      <div className="flex items-center">
                        <input
                          type="checkbox"
                          name="isActive"
                          id="isActive"
                          checked={isActive}
                          onChange={(e) => setIsActive(e.target.checked)}
                          className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                        />
                        <label
                          htmlFor="isActive"
                          className="ml-2 text-sm text-gray-700 cursor-pointer"
                        >
                          Active
                        </label>
                      </div>
                    )}
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
